import { cva, type VariantProps } from "class-variance-authority";
import {
  forwardRef,
  type MouseEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
  useId,
} from "react";
import { useField } from "remix-validated-form";

import { FieldError, FieldInfo } from "~/components/ui/forms/field-messages";
import { Label } from "~/components/ui/forms/label";
import { cn } from "~/utils/classnames";

const textareaStyles = cva(
  [
    "block w-full resize-none rounded-2xl border border-grey-300 placeholder:text-grey-400",
    "aria-invalid:border-danger-500 aria-invalid:text-danger-700 aria-invalid:ring-danger-500 aria-invalid:placeholder:text-danger-300",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      size: {
        xs: "min-h-6 px-3 py-1 text-xs",
        sm: "min-h-8 px-4 py-1.5 text-sm",
        default: "min-h-10 px-4 py-2 text-sm",
        lg: "min-h-12 px-5 py-3 text-base",
      },
      iconPosition: {
        left: "pl-11",
        right: "pr-11",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

const iconStyles = cva(
  "absolute inset-y-0 flex items-end justify-center pb-3 text-xl text-grey-400",
  {
    variants: {
      size: {
        default: "",
        xs: "",
        sm: "",
        lg: "",
      },
      iconPosition: {
        left: "left-0 pl-3",
        right: "right-0 pr-3",
      },
    },
    defaultVariants: {
      size: "default",
      iconPosition: "right",
    },
  }
);

interface TextareaProps
  extends VariantProps<typeof textareaStyles>,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
  label?: string;
  errorMessage?: string;
  infoMessage?: string;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  onIconClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  absoluteComponent?: ReactNode;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    id,
    label,
    infoMessage,
    errorMessage,
    icon,
    iconPosition = "right",
    onIconClick,
    absoluteComponent,
    size,
    className,
    ...props
  },
  ref
) {
  const genId = useId();

  return (
    <div className={cn("group", className)} aria-disabled={props.disabled}>
      {label ? (
        <Label htmlFor={id || genId} required={props.required} inputSize={size}>
          {label}
        </Label>
      ) : null}
      <div className={cn("relative", label && "mt-1.5")}>
        <textarea
          id={id || genId}
          rows={props.rows || 6}
          aria-invalid={!!errorMessage}
          aria-describedby={`${id || genId}-info ${id || genId}-error`}
          className={cn(textareaStyles({ size, iconPosition: icon ? iconPosition : null }))}
          ref={ref}
          {...props}
        />
        {icon ? (
          <div className={cn(iconStyles({ size, iconPosition }))}>
            {onIconClick ? (
              <button
                type="button"
                onClick={onIconClick}
                aria-label={`Valider le champ ${label || props.name}`}
              >
                {icon}
              </button>
            ) : (
              icon
            )}
          </div>
        ) : null}
        {absoluteComponent ? absoluteComponent : null}
      </div>
      {infoMessage ? (
        <FieldInfo inputId={id || genId} inputSize={size}>
          {infoMessage}
        </FieldInfo>
      ) : null}
      {errorMessage ? (
        <FieldError inputId={id || genId} inputSize={size}>
          {errorMessage}
        </FieldError>
      ) : null}
    </div>
  );
});

export const ValidatedTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function ValidatedTextarea(props, ref) {
    const { error, getInputProps } = useField(props.name ?? "");

    return <Textarea ref={ref} {...props} {...getInputProps({ errorMessage: error, ...props })} />;
  }
);
